import { Label, ValidateHint } from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { Select } from "molecules/select/Select";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { CompanyDomain } from "src/common/company-domain/types.ts";
import type {
	ErLegalEntity,
	FusionhubIntegration,
} from "src/common/service/fusionHub/types.ts";
import type { LegalEntity } from "src/common/service/nexus/endpoints/companyDomain/types.ts";
import type { Integration as NexusIntegration } from "src/common/service/nexus/endpoints/integration/types.ts";
import {
	useAddIntegrationToLegalEntityMutation,
	useUpsertLegalEntityFromFusionhubIntegrationsMutation,
} from "src/common/service/nexus/nexusApi";
import { useCallback, useEffect } from "react";
import { HeaderLayout } from "src/routes/HeaderLayout.js";
import {
	Card,
	CardHeader,
	CardItemsHeader,
} from "src/features/planning/components/Card.tsx";

import { Navigate, useNavigate } from "react-router";
import { useAppDispatch } from "../../../common/redux/store";
import {
	fusionHubApi,
	useTriggerSyncMutation,
} from "src/common/service/fusionHub/fusionHubApi.ts";
import { firstOrThrow } from "../../../common/utils/firstOrThrow";
import { useDataSourcesBasePath } from "src/features/integrations/useDataSourcesBasePath.js";

type Props = {
	companyDomain: CompanyDomain;
	integrationId: string;
	integration: FusionhubIntegration;
	existingIntegrations: NexusIntegration[];
	integrationLegalEntities: ErLegalEntity[];
	legalEntities: LegalEntity[];
	autoResolve: boolean;
};

export const LegalEntityIntegration = ({
	companyDomain,
	integrationId,
	integration,
	integrationLegalEntities,
	legalEntities,
	existingIntegrations,
	autoResolve,
}: Props) => {
	const { t } = useTranslation();
	const dataSourcesBase = useDataSourcesBasePath();

	const navigate = useNavigate();

	const [addIntegration] = useAddIntegrationToLegalEntityMutation();
	const [triggerSync, { isLoading: isTriggeringSync }] =
		useTriggerSyncMutation();
	const dispatch = useAppDispatch();
	const primaryLegalEntity = integrationLegalEntities[0] ?? null;
	const hasSingleIntegrationLegalEntity = integrationLegalEntities.length === 1;

	const legalEntitiesWithoutIntegration = legalEntities.filter(
		(legalEntity) =>
			!existingIntegrations.some(
				(integration) => integration.legalEntityId === legalEntity.id,
			),
	);

	const addIntegrationToLegalEntity = useCallback(
		async (legalEntityId: string, triggerSuccessToast: boolean) => {
			const selectedLegalEntity = legalEntities.find(
				(legalEntity) => legalEntity.id === legalEntityId,
			);

			const addIntegrationResult = await addIntegration({
				companyDomainId: companyDomain.id,
				legalEntityId,
				integrationId,
			});

			if ("error" in addIntegrationResult) {
				showErrorNotification({
					message: t("Failed to add integration to legal entity"),
				});
				return;
			}

			dispatch(fusionHubApi.util.invalidateTags(["Integrations"]));
			if (triggerSuccessToast && selectedLegalEntity) {
				showSuccessNotification({
					message: t("Connected data source to {{name}}", {
						name: selectedLegalEntity.name,
					}),
				});
			}
		},
		[
			addIntegration,
			companyDomain.id,
			integrationId,
			t,
			legalEntities,
			dispatch,
		],
	);

	const [createLegalEntity, { isLoading: isUpsertingLegalEntity }] =
		useUpsertLegalEntityFromFusionhubIntegrationsMutation();

	const createAndConnect = useCallback(
		async (triggerSuccessToast: boolean) => {
			navigate(dataSourcesBase);

			const createLegalEntityResult = await createLegalEntity({
				companyDomainId: companyDomain.id,
				integrationIds: [Number(integrationId)],
			});

			if ("error" in createLegalEntityResult) {
				showErrorNotification({
					message: t("Failed to create legal entity"),
				});
				return;
			}

			if (triggerSuccessToast) {
				if (hasSingleIntegrationLegalEntity) {
					showSuccessNotification({
						message: t("Created legal entity {{name}}", {
							name: firstOrThrow(createLegalEntityResult.data).name,
						}),
					});
				} else {
					showSuccessNotification({
						message: t("Created {{count}} legal entities", {
							count: createLegalEntityResult.data.length,
						}),
					});
				}
			}
		},
		[
			companyDomain.id,
			createLegalEntity,
			t,
			navigate,
			integrationId,
			hasSingleIntegrationLegalEntity,
			dataSourcesBase,
		],
	);

	const existingLegalEntityForm = useForm({
		defaultValues: {
			legalEntityId: "",
		},
	});

	useEffect(() => {
		if (autoResolve && integrationLegalEntities.length > 0) {
			createAndConnect(false);
		}
	}, [autoResolve, createAndConnect, integrationLegalEntities.length]);

	const integrationIsConnectedAlready = existingIntegrations.some(
		(integration) =>
			integration.source === "FusionHub" &&
			integration.sourceId === integrationId,
	);

	if (integrationIsConnectedAlready) {
		return <Navigate to={dataSourcesBase} />;
	}

	if (integrationLegalEntities.length === 0) {
		if (integration.loadStatus === "FAILED") {
			return (
				<HeaderLayout title={t("Data source sync failed")}>
					<Card>
						<CardHeader>
							<CardItemsHeader
								heading={t("Company data could not be loaded")}
							/>
						</CardHeader>
						<div className="flex flex-col gap-4 p-4">
							<div className="text-gray-700">
								{t(
									"The initial sync failed before any legal entities were discovered. Start a new sync and reload this page when it has completed.",
								)}
							</div>
							<Button
								className="align-self-start"
								onClick={async () => {
									const result = await triggerSync({
										integrationIds: [integration.id],
									});

									if ("error" in result) {
										showErrorNotification({
											message: t("Failed to trigger resync"),
										});
										return;
									}

									showSuccessNotification({
										message: t("Resync triggered"),
									});
								}}
								isLoading={isTriggeringSync}
							>
								{t("Retry sync")}
							</Button>
						</div>
					</Card>
				</HeaderLayout>
			);
		}

		return (
			<HeaderLayout title={t("Data source is syncing")}>
				<Card>
					<CardHeader>
						<CardItemsHeader heading={t("Waiting for company data")} />
					</CardHeader>
					<div className="p-4 text-gray-700">
						{t(
							"The data source has been created, but the initial sync has not discovered any legal entities yet. Reload this page when the sync has completed.",
						)}
					</div>
				</Card>
			</HeaderLayout>
		);
	}

	return (
		<HeaderLayout
			title={t('Finish setup of "{{legalEntityName}}"', {
				legalEntityName: primaryLegalEntity?.name ?? integrationId,
			})}
		>
			<div className="flex flex-col gap-4">
				{hasSingleIntegrationLegalEntity &&
					legalEntitiesWithoutIntegration.length > 0 && (
						<Card>
							<CardHeader>
								<CardItemsHeader
									heading={t("Connect to existing legal entity")}
								/>
							</CardHeader>
							<form
								className="flex flex-col gap-2 p-4"
								onSubmit={existingLegalEntityForm.handleSubmit(
									async ({ legalEntityId }) => {
										await addIntegrationToLegalEntity(legalEntityId, true);
									},
								)}
							>
								<Controller
									control={existingLegalEntityForm.control}
									name="legalEntityId"
									rules={{
										required: t("This field is required"),
									}}
									render={({ field, fieldState }) => {
										return (
											<div className="mb-4">
												<Label>{t("Select legal entity")}</Label>
												<Select
													options={legalEntitiesWithoutIntegration.map(
														(legalEntity) => ({
															label: legalEntity.name,
															value: legalEntity.id,
														}),
													)}
													placeholder=""
													ariaLabel={t("Select legal entity")}
													{...field}
												/>
												{fieldState.error && (
													<ValidateHint error>
														{fieldState.error.message}
													</ValidateHint>
												)}
											</div>
										);
									}}
								/>
								<Button
									className="align-self-start"
									type="submit"
									isLoading={existingLegalEntityForm.formState.isSubmitting}
								>
									{t("Connect")}
								</Button>
							</form>
						</Card>
					)}
				<Card>
					<CardHeader>
						<CardItemsHeader
							heading={
								hasSingleIntegrationLegalEntity
									? t("Create a new legal entity")
									: t("Create legal entities from AFAS")
							}
						/>
					</CardHeader>
					{!hasSingleIntegrationLegalEntity ? (
						<div className="px-4 pb-2 text-gray-700">
							{t(
								"All discovered legal entities from this AFAS integration will be created in the selected company domain.",
							)}
						</div>
					) : null}
					<Button
						className="align-self-start"
						onClick={async () => {
							await createAndConnect(true);
						}}
						isLoading={isUpsertingLegalEntity}
					>
						{hasSingleIntegrationLegalEntity ? t("Create") : t("Create all")}
					</Button>
				</Card>
			</div>
		</HeaderLayout>
	);
};
